import _async_to_generator from "@swc/helpers/src/_async_to_generator.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import _ts_generator from "@swc/helpers/src/_ts_generator.mjs";
import { create } from "zustand";
import axios from "axios";
import { getToken } from "@cinch-labs/shared-util";
import { Env, readFromEnv } from "@cinch-nx/environments";
import { addReservationToOrder, handleExtendReservation } from "@cinch-labs/data-checkout";
import { checkCinchCareEligibility } from "./helpers/check-cinchcare-eligibility";
import { mapCinchCareOrderToVehicle } from "./helpers/map-cinch-care-order-to-vehicle";
import { getReservationsDecouplingPhase } from "./helpers/reservations-decoupling";
import { getReservationServiceUrl } from "./helpers/get-reservation-service-config";
import { checkEMaCServiceWarrantyEligibility } from "./helpers/check-emac-servce-warranty-eligibility";
var ordersConfig = {
    orderServiceUrl: "",
    cinchCareServiceUrl: "",
    authKey: "",
    legacyAuthKey: ""
};
var httpClient = axios;
export var initOrdersStore = function(config) {
    var axiosImpl = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : axios;
    ordersConfig = _object_spread({
        orderServiceUrl: readFromEnv(Env.OrderServiceUrl),
        cinchCareServiceUrl: readFromEnv(Env.CinchcareServiceUrl),
        authKey: readFromEnv(Env.AuthKey),
        legacyAuthKey: readFromEnv(Env.LegacyAuthKey)
    }, config);
    var _httpClient = axiosImpl.create({
        baseURL: ordersConfig.orderServiceUrl
    });
    _httpClient.interceptors.request.use(function(request) {
        var token = getToken({
            authKey: ordersConfig.authKey,
            legacyAuthKey: ordersConfig.legacyAuthKey
        });
        return _object_spread_props(_object_spread({}, request), {
            headers: _object_spread_props(_object_spread({}, request.headers), {
                Authorization: "Bearer ".concat(token === null || token === void 0 ? void 0 : token.access_token)
            })
        });
    });
    httpClient = _httpClient;
};
/**
 * @description
 * Vehicle orders and cinchCare orders are dependent upon one another
 * cinchCare orders require the vehicle object from the order, matched by vehicle VRM
 * vehicle orders have a boolean flag to indicate their eligibility for cinchCare
 *
 * Because of this cross dependency, the functions getVehicleOrders and getCinchCareOrders
 * should be used to retrieve the enriched objects from the store instead of accessing the
 * raw response arrays inside vehicleOrdersRequest and cinchCareOrdersRequest.
 *
 * These functions will enrich the raw response each time they are requested from the store,
 * ensuring the most up-to-date data
 */ export var useOrdersStore = create(function(set, get) {
    return {
        vehicleOrdersRequest: {
            status: "pending",
            orders: []
        },
        cinchCareOrdersRequest: {
            status: "pending",
            orders: []
        },
        activeOrder: undefined,
        vehicleOrderAddingCinchCare: undefined,
        setVehicleOrderAddingCinchCare: function(order) {
            return set({
                vehicleOrderAddingCinchCare: order
            });
        },
        refreshActiveOrder: /*#__PURE__*/ _async_to_generator(function() {
            var reservationServiceUrl, reservationsDecouplingPhase, ref, latestActiveOrder, token, activeOrderWithReservation;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        reservationServiceUrl = getReservationServiceUrl();
                        reservationsDecouplingPhase = getReservationsDecouplingPhase();
                        return [
                            4,
                            httpClient.get("/orders/latest/active")
                        ];
                    case 1:
                        ref = _state.sent(), latestActiveOrder = ref.data.latestActiveOrder;
                        token = getToken({
                            authKey: readFromEnv(Env.AuthKey),
                            legacyAuthKey: readFromEnv(Env.LegacyAuthKey)
                        });
                        return [
                            4,
                            addReservationToOrder({
                                order: latestActiveOrder,
                                token: token === null || token === void 0 ? void 0 : token.access_token,
                                reservationServiceUrl: reservationServiceUrl,
                                reservationsDecouplingPhase: reservationsDecouplingPhase
                            }).catch(function(error) {
                                throw error;
                            })
                        ];
                    case 2:
                        activeOrderWithReservation = _state.sent();
                        set(function() {
                            return {
                                activeOrder: activeOrderWithReservation
                            };
                        });
                        return [
                            2
                        ];
                }
            });
        }),
        checkIfVehicleIsReserved: function() {
            var _ref = _async_to_generator(function(vehicleId) {
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            if (!vehicleId) {
                                return [
                                    2,
                                    {
                                        reserved: true
                                    }
                                ];
                            }
                            return [
                                4,
                                httpClient.get("/reservation-status/".concat(vehicleId, "?userId="))
                            ];
                        case 1:
                            return [
                                2,
                                _state.sent().data
                            ];
                    }
                });
            });
            return function(vehicleId) {
                return _ref.apply(this, arguments);
            };
        }(),
        extendOrderExpiryDate: function() {
            var _ref = _async_to_generator(function(orderId, token) {
                var reservationServiceUrl, reservationsDecouplingPhase, e;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            reservationServiceUrl = getReservationServiceUrl();
                            reservationsDecouplingPhase = getReservationsDecouplingPhase();
                            _state.label = 1;
                        case 1:
                            _state.trys.push([
                                1,
                                4,
                                ,
                                5
                            ]);
                            return [
                                4,
                                handleExtendReservation({
                                    orderId: orderId,
                                    token: token,
                                    orderServiceUrl: readFromEnv(Env.OrderServiceUrl),
                                    reservationServiceUrl: reservationServiceUrl,
                                    reservationsDecouplingPhase: reservationsDecouplingPhase
                                })
                            ];
                        case 2:
                            _state.sent();
                            return [
                                4,
                                get().fetchOrders()
                            ];
                        case 3:
                            _state.sent();
                            return [
                                3,
                                5
                            ];
                        case 4:
                            e = _state.sent();
                            return [
                                2,
                                set(function() {
                                    return {
                                        vehicleOrdersRequest: {
                                            status: "invalid",
                                            orders: undefined
                                        }
                                    };
                                })
                            ];
                        case 5:
                            return [
                                2
                            ];
                    }
                });
            });
            return function(orderId, token) {
                return _ref.apply(this, arguments);
            };
        }(),
        cancelOrder: function() {
            var _ref = _async_to_generator(function(orderId, cancelOrderReason, token) {
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            return [
                                4,
                                httpClient.post("/order/cancel-order", {
                                    orderId: orderId,
                                    cancelOrderReason: cancelOrderReason
                                }, {
                                    headers: {
                                        Authorization: "Bearer ".concat(token)
                                    }
                                })
                            ];
                        case 1:
                            return [
                                2,
                                _state.sent()
                            ];
                    }
                });
            });
            return function(orderId, cancelOrderReason, token) {
                return _ref.apply(this, arguments);
            };
        }(),
        cinchCareOrders: function() {
            var ref = get(), _vehicleOrdersRequest = ref.vehicleOrdersRequest, vehicleOrders = _vehicleOrdersRequest.orders, _cinchCareOrdersRequest = ref.cinchCareOrdersRequest, cinchCareOrders = _cinchCareOrdersRequest.orders;
            return _to_consumable_array(cinchCareOrders !== null && cinchCareOrders !== void 0 ? cinchCareOrders : []).map(function(cinchCareOrder) {
                return mapCinchCareOrderToVehicle(cinchCareOrder, vehicleOrders);
            });
        },
        vehicleOrders: function() {
            var ref = get(), _vehicleOrdersRequest = ref.vehicleOrdersRequest, vehicleOrders = _vehicleOrdersRequest.orders, _cinchCareOrdersRequest = ref.cinchCareOrdersRequest, cinchCareOrders = _cinchCareOrdersRequest.orders;
            return _to_consumable_array(vehicleOrders !== null && vehicleOrders !== void 0 ? vehicleOrders : []).map(function(order) {
                return {
                    order: order,
                    eligibleForCinchCare: checkCinchCareEligibility(order, cinchCareOrders),
                    eligibleForEMaCServiceWarranty: checkEMaCServiceWarrantyEligibility(order, cinchCareOrders)
                };
            });
        },
        vehicleOrdersByStatus: function() {
            for(var _len = arguments.length, statuses = new Array(_len), _key = 0; _key < _len; _key++){
                statuses[_key] = arguments[_key];
            }
            var ref;
            return (ref = get().vehicleOrders()) === null || ref === void 0 ? void 0 : ref.filter(function(vehicleOrder) {
                return statuses.includes(vehicleOrder.order.orderStatus);
            });
        },
        fetchOrders: /*#__PURE__*/ _async_to_generator(function() {
            var orders, e;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            2,
                            ,
                            3
                        ]);
                        return [
                            4,
                            httpClient.get("/orders")
                        ];
                    case 1:
                        orders = _state.sent().data.orders;
                        return [
                            2,
                            set(function() {
                                return {
                                    vehicleOrdersRequest: {
                                        status: "valid",
                                        orders: orders
                                    }
                                };
                            })
                        ];
                    case 2:
                        e = _state.sent();
                        return [
                            2,
                            set(function() {
                                return {
                                    vehicleOrdersRequest: {
                                        status: "invalid",
                                        orders: undefined
                                    }
                                };
                            })
                        ];
                    case 3:
                        return [
                            2
                        ];
                }
            });
        }),
        fetchCinchCareOrders: /*#__PURE__*/ _async_to_generator(function() {
            var cinchCareServiceUrl, ref, cinchCareOrders, e;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            2,
                            ,
                            3
                        ]);
                        cinchCareServiceUrl = ordersConfig.cinchCareServiceUrl;
                        return [
                            4,
                            httpClient.get("/orderdetails", {
                                baseURL: cinchCareServiceUrl
                            })
                        ];
                    case 1:
                        ref = _state.sent(), cinchCareOrders = ref.data;
                        set(function() {
                            return {
                                cinchCareOrdersRequest: {
                                    status: "valid",
                                    orders: cinchCareOrders
                                }
                            };
                        });
                        return [
                            2
                        ];
                    case 2:
                        e = _state.sent();
                        set(function() {
                            return {
                                cinchCareOrdersRequest: {
                                    status: "invalid",
                                    orders: undefined
                                }
                            };
                        });
                        return [
                            2
                        ];
                    case 3:
                        return [
                            2
                        ];
                }
            });
        }),
        activateCinchCare: function() {
            var _ref = _async_to_generator(function(payload) {
                var cinchCareServiceUrl, activateCinchCareUrl;
                return _ts_generator(this, function(_state) {
                    cinchCareServiceUrl = ordersConfig.cinchCareServiceUrl;
                    activateCinchCareUrl = "/cinchcare/activate";
                    return [
                        2,
                        httpClient.post(activateCinchCareUrl, payload, {
                            baseURL: cinchCareServiceUrl
                        })
                    ];
                });
            });
            return function(payload) {
                return _ref.apply(this, arguments);
            };
        }()
    };
});
